import type { ReactElement, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import type { SheetGame } from '@/app/[locale]/match/libs/scoreSheet';
import { leadPath, matchHighlights } from '@/app/[locale]/match/libs/matchHighlights';

export interface MatchWhoLedProps {
  games: SheetGame[];
  /** The two side names, as the key line and the highlight tiles print them. */
  sides: [string, string];
}

// The chart's own coordinate space. x is in rallies, so the path needs no pixel width: the SVG is
// stretched to its box (`preserveAspectRatio="none"`) and the strokes stay 1px and 2px through
// `vector-effect: non-scaling-stroke`. y is fixed at 72 units, the box's 72px height.
const H = 72;
const MID = H / 2;
const PAD = 6;

const score = (s: [number, number]) => `${s[0]}–${s[1]}`;
const num = (chunks: ReactNode) => <Numeric>{chunks}</Numeric>;

// Literal classes: Tailwind v4 generates no CSS for interpolated names.
// One chart column per charted game up to three; a fourth or fifth game wraps to a new row.
const CHART_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' } as const;
const TILE_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 @md:grid-cols-2',
  3: 'grid-cols-1 @md:grid-cols-3',
} as const;

/**
 * One step chart per game (y = score difference, above the line = the first side ahead), the game's
 * score above each, then the match highlights. Renders nothing when no game has points.
 */
export function MatchWhoLed({ games, sides }: MatchWhoLedProps): ReactElement | null {
  const t = useTranslations('Scorecard');

  const charts = games
    .map((game) => ({ game, path: leadPath(game) }))
    .filter((c) => c.path.length > 0);
  if (charts.length === 0) return null;

  // One scale for every game, so a long game reads long and a big lead reads big in all of them.
  const maxLen = Math.max(...charts.map((c) => c.path.length));
  const yMax = Math.max(1, ...charts.flatMap((c) => c.path.map(Math.abs)));
  const y = (lead: number) => MID - (lead / yMax) * (MID - PAD);

  const { run, lead, comeback } = matchHighlights(charts.map((c) => c.game));
  const tiles = [
    run && (
      <div key="run" data-highlight="run" className="bg-surface min-w-0 rounded-md px-2.5 py-2">
        <p className="text-muted text-xs">{t('longestRun')}</p>
        <p className="text-[15px] font-bold">
          {t.rich('longestRunValue', { count: run.length, n: num })}
        </p>
        <p className={cn('text-muted text-xs', NAME_WRAP)}>
          {t.rich('highlightWhere', { name: sides[run.side], game: run.game, n: num })}
        </p>
      </div>
    ),
    lead && (
      <div key="lead" data-highlight="lead" className="bg-surface min-w-0 rounded-md px-2.5 py-2">
        <p className="text-muted text-xs">{t('biggestLead')}</p>
        <p className="text-[15px] font-bold">
          {t.rich('biggestLeadValue', { count: lead.points, n: num })}
        </p>
        <p className={cn('text-muted text-xs', NAME_WRAP)}>
          {t.rich('highlightWhereAt', {
            name: sides[lead.side],
            game: lead.game,
            score: score(lead.score),
            n: num,
          })}
        </p>
      </div>
    ),
    comeback && (
      <div
        key="comeback"
        data-highlight="comeback"
        className="bg-surface min-w-0 rounded-md px-2.5 py-2"
      >
        <p className="text-muted text-xs">{t('biggestComeback')}</p>
        <p className="text-[15px] font-bold">
          <Numeric>{score(comeback.from)}</Numeric> → <Numeric>{score(comeback.to)}</Numeric>
        </p>
        <p className={cn('text-muted text-xs', NAME_WRAP)}>
          {t.rich('highlightWhere', { name: sides[comeback.side], game: comeback.game, n: num })}
        </p>
      </div>
    ),
  ].filter(Boolean);
  const tileCols = TILE_COLS[Math.min(tiles.length, 3) as 1 | 2 | 3];

  return (
    <div className="@container">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 pt-2.5 pb-2">
        <h3 className="text-foreground text-sm font-semibold">{t('whoLed')}</h3>
        <p className={cn('text-muted text-xs', NAME_WRAP)}>
          {t('whoLedKey', { a: sides[0], b: sides[1] })}
        </p>
      </div>

      <div
        className={cn(
          'grid gap-2 px-3 pb-2.5',
          CHART_COLS[Math.min(charts.length, 3) as 1 | 2 | 3],
        )}
      >
        {charts.map(({ game, path }) => {
          const total = score(game.score);
          const lastY = y(path[path.length - 1]);
          const d = path.reduce(
            (acc, lead, k) => `${acc} H${k + 1} V${y(lead).toFixed(2)}`,
            `M0 ${MID}`,
          );
          return (
            <div key={game.number} className="min-w-0">
              <h4
                data-flow-head
                className="mb-0.5 flex flex-wrap items-baseline gap-x-1.5 text-xs font-semibold"
              >
                <Numeric>{t('gameShort', { number: game.number })}</Numeric>
                <Numeric>{total}</Numeric>
              </h4>
              <div className="relative h-[72px]">
                <svg
                  role="img"
                  aria-label={t('chartLabel', { number: game.number, score: total })}
                  viewBox={`0 0 ${maxLen} ${H}`}
                  preserveAspectRatio="none"
                  className="absolute inset-0 h-full w-full overflow-visible"
                >
                  <line
                    x1={0}
                    x2={maxLen}
                    y1={MID}
                    y2={MID}
                    strokeWidth={1}
                    className="stroke-border-strong [vector-effect:non-scaling-stroke]"
                  />
                  <path
                    data-flow-path
                    d={d}
                    fill="none"
                    strokeWidth={2}
                    strokeLinejoin="round"
                    className="stroke-foreground [vector-effect:non-scaling-stroke]"
                  />
                </svg>
                {/* HTML, not an SVG circle: the stretched viewBox would make a circle an ellipse. */}
                <span
                  aria-hidden="true"
                  data-flow-dot
                  style={{ left: `${(path.length / maxLen) * 100}%`, top: `${(lastY / H) * 100}%` }}
                  className="bg-foreground border-card absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={cn('grid gap-2 px-3 pb-3', tileCols)}>{tiles}</div>
    </div>
  );
}
